import React, { Fragment, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  Dropdown,
  Input,
  InputOnChangeData,
  Pagination,
  Table,
} from 'semantic-ui-react';
import deburr from 'lodash/deburr';
import { useTable } from 'hooks/useTable';
import { pageSizeOptions } from 'hooks/useTablePaginating';
import { ApplicationSegment } from 'components/ApplicationSegment';
import { useHistory } from 'react-router-dom';
import { EmptyStateMessage } from 'components/EmptyStateMessage';
import { useResponsive } from 'hooks/useResponsive';
import { UserSummary } from 'modules/user/types';
import { useRoleSets } from 'modules/user/hooks/useRoleSets';
import { ActiveIndicator } from 'components/ActiveIndicator';
import { ApplicationLoader } from 'components/ApplicationLoader';
import { ShowInactiveToggle } from 'components/ShowInactiveToggle';

const m = defineMessages({
  recordperpage: {
    id: 'Users.Table.RecordPerPage',
    defaultMessage: 'Utilisateurs par page',
  },
  totalCount: {
    id: 'Users.Table.TotalCount',
    defaultMessage: 'Nombre total',
  },
  search: {
    id: 'Users.Table.Search',
    defaultMessage: 'Rechercher',
  },
  noRow: {
    id: 'Users.Table.NoRow',
    defaultMessage: "Il n'y a aucun utilisateur.",
  },
  noMatchFound: {
    id: 'Users.Table.NoMatchFound',
    defaultMessage: 'Aucun utilisateur ne correspond à votre recherche.',
  },
  fullName: {
    id: 'Users.Table.FullName',
    defaultMessage: 'Nom',
  },
  team: {
    id: 'Users.Table.Team',
    defaultMessage: 'Équipe',
  },
  role: {
    id: 'Users.Table.Role',
    defaultMessage: 'Role',
  },
  active: {
    id: 'Users.Table.Active',
    defaultMessage: 'Actif',
  },
});

interface EntryRow {
  id: string;
  fullName: string;
  teamName: string;
  roleSetName: string | undefined;
  isActive: boolean;
}

interface Props {
  users: UserSummary[];
}

export const UsersTable: React.FC<Props> = ({ users }) => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const { isMobile } = useResponsive();
  const { getMatchingRoleSet } = useRoleSets();
  const {
    currentPageRows,
    setRows,
    hasRows,
    matchFound,
    hasMultiplePages,
    pageCount,
    sortingColumn,
    sortingDirection,
    searchInRows,
    changeSortingColumn,
    currentPage,
    handlePageChange,
    filteredRows,
    pageSize,
    handlePageSizeChange,
    areRowsReady,
    showInactive,
    setShowInactive,
  } = useTable<EntryRow>({
    sortingColumn: 'fullName',
    sortingDirection: 'ascending',
  });

  const isLoaded = areRowsReady;

  useEffect(() => {
    const rows: EntryRow[] = users.map((u) => ({
      id: u.id,
      fullName: u.fullName,
      teamName: u.teamName,
      roleSetName: getMatchingRoleSet(u.roleNames)?.displayName,
      isActive: u.isActive,
    }));
    setRows(rows);
  }, [users, setRows, getMatchingRoleSet]);

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData
  ) => {
    searchInRows(data.value, (entryRow) =>
      deburr(
        Object.values([
          entryRow.id,
          entryRow.fullName,
          entryRow.teamName,
          entryRow.roleSetName,
        ]).toString()
      )
    );
  };

  const handleRowClick = (id: string) => {
    history.push(`/admin/users/${id}`);
  };

  if (!isLoaded) {
    return <ApplicationLoader />;
  }

  const totalCountAndRecordPerPage = matchFound && (
    <Fragment>
      <div>
        {formatMessage(m.recordperpage)}:{' '}
        <Dropdown
          inline
          options={pageSizeOptions}
          value={pageSize}
          onChange={handlePageSizeChange}
        />
      </div>
      <div>
        {formatMessage(m.totalCount)}: {filteredRows.length}
      </div>
    </Fragment>
  );

  const searchInput = (
    <Input
      icon="search"
      placeholder={formatMessage(m.search)}
      onChange={handleSearchChange}
    />
  );

  const topBar = hasRows && (
    <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
      <div className="tw-flex-1">{totalCountAndRecordPerPage}</div>
      <div className="tw-flex tw-items-center">
        <ShowInactiveToggle
          showInactive={showInactive}
          onChange={setShowInactive}
        />
        {searchInput}
      </div>
    </div>
  );

  const table = (
    <Fragment>
      <Table
        sortable
        celled
        striped
        selectable
        size="small"
        className="text-sm"
      >
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell
              sorted={
                sortingColumn === 'fullName' ? sortingDirection : undefined
              }
              onClick={changeSortingColumn('fullName')}
            >
              {formatMessage(m.fullName)}
            </Table.HeaderCell>
            <Table.HeaderCell
              sorted={
                sortingColumn === 'teamName' ? sortingDirection : undefined
              }
              onClick={changeSortingColumn('teamName')}
              collapsing
            >
              {formatMessage(m.team)}
            </Table.HeaderCell>
            <Table.HeaderCell
              sorted={
                sortingColumn === 'roleSetName' ? sortingDirection : undefined
              }
              onClick={changeSortingColumn('roleSetName')}
              collapsing
            >
              {formatMessage(m.role)}
            </Table.HeaderCell>

            {showInactive && (
              <Table.HeaderCell
                sorted={
                  sortingColumn === 'isActive' ? sortingDirection : undefined
                }
                onClick={changeSortingColumn('isActive')}
                collapsing
              >
                {formatMessage(m.active)}
              </Table.HeaderCell>
            )}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {currentPageRows.map((row) => (
            <Table.Row
              key={row.id}
              onClick={() => handleRowClick(row.id)}
              className="tw-cursor-pointer"
            >
              <Table.Cell>{row.fullName}</Table.Cell>
              <Table.Cell collapsing>{row.teamName}</Table.Cell>
              <Table.Cell collapsing>{row.roleSetName}</Table.Cell>
              
              {showInactive && (
                <Table.Cell collapsing>
                  <div className="tw-flex tw-justify-center">
                    <ActiveIndicator active={row.isActive} />
                  </div>
                </Table.Cell>
              )}
            </Table.Row>
          ))}
          {!hasRows && (
            <Table.Row>
              <Table.Cell colSpan="4">
                <EmptyStateMessage message={formatMessage(m.noRow)} />
              </Table.Cell>
            </Table.Row>
          )}

          {hasRows && !matchFound && (
            <Table.Row>
              <Table.Cell colSpan="4">
                <EmptyStateMessage message={formatMessage(m.noMatchFound)} />
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    </Fragment>
  );

  const pagination = hasMultiplePages && (
    <Pagination
      totalPages={pageCount}
      activePage={currentPage}
      onPageChange={handlePageChange}
    />
  );

  const bottomBarCenteringClass = isMobile ? 'tw-justify-center' : undefined;

  const bottomBar = (
    <div className={`tw-flex tw-items-baseline ${bottomBarCenteringClass}`}>
      {pagination}
    </div>
  );

  return (
    <Fragment>
      <ApplicationSegment>
        {topBar}
        {table}
        {bottomBar}
      </ApplicationSegment>
    </Fragment>
  );
};
